import { ModalViewModel } from '@/infra/ModalViewModel'
import { action, computed, observable } from 'mobx'
import { type Id } from '@/infra/id'
import { type SearchableUser } from '@/features/iam/users/SearchableUser'
import { Workspace } from '@/features/iam/workspaces/Workspace'
import type { AlertErrorHandlerFactory, AlertFactory } from '@/features/shell'
import { task } from 'mobx-task'
import { pluralize } from '@/utils/pluralization-util'
import type { UpdateMemberFn } from '@/features/iam'

export class RestoreToWorkspaceDialogViewModel {
  readonly modal = new ModalViewModel()

  @observable
  private accessor _selectedUserIds = new Set<Id>()

  @observable
  accessor selectedUser: SearchableUser | undefined = undefined

  @observable
  accessor workspace: Workspace = Workspace.none

  constructor(
    private readonly _onRestored: ((userPublicIds: Id[]) => void) | undefined,
    private readonly _updateMember: UpdateMemberFn,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
  ) {}

  @computed
  get selectedUserIds() {
    return Array.from(this._selectedUserIds)
  }

  @computed
  get selectedUserName() {
    return this.selectedUser ? this.selectedUser.displayName : null
  }

  @action.bound
  showWithUser(user: SearchableUser, workspace: Workspace) {
    this.selectedUser = user
    this._selectedUserIds = new Set([user.publicId])
    this.workspace = workspace
    this.modal.show()
  }

  @action.bound
  open(selectedUserIds: Set<Id>, workspace: Workspace) {
    this.selectedUser = undefined
    this._selectedUserIds = selectedUserIds
    this.workspace = workspace
    this.modal.show()
  }

  restore = task.resolved(async () => {
    const alert = this._newAlert()
      .withState('pending')
      .saying(`Restoring ${pluralize('User', this.selectedUserIds.length)}!`)
      .autoDismiss()
      .show()

    try {
      await Promise.all(
        this.selectedUserIds.map((userPublicId) =>
          this._updateMember({
            workspaceId: this.workspace.id,
            userPublicId,
            inactive: false,
          }),
        ),
      )
    } catch (err) {
      alert.dismiss()
      this._createAlertErrorHandler('Restoring users failed')(err)
    }

    this._onRestored?.(this.selectedUserIds)

    alert
      .withState('success')
      .saying(
        'Success!',
        `${pluralize('User', this.selectedUserIds.length)} restored successfully!`,
      )
      .autoDismiss()
      .show()

    this.close()
  })

  @action.bound
  close() {
    this.workspace = Workspace.none
    this.selectedUser = undefined
    this.modal.close()
  }
}
